import type {ComponentEnhancer} from 'recompose'
import {branch} from 'recompose'

import type {IconType} from '@jetbrains/ring-ui-built/components/icon/icon'
import {SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import classnames from 'classnames'
import compose from 'lodash/flowRight'
import * as React from 'react'

import {withVisibility} from '../Visible/Visible.hocs'

import {allIconNames, getIconAndClassNames} from './automaton'
import {passthroughIcons} from './Icon.passthrough'

import iconStyles from './Icon.module.css'

export const allIcons = allIconNames
type Size = 'S' | 'M'
type OwnProps = {
  name: string
  className?: string
  isVisible?: boolean
  size?: Size
  title?: string
  'aria-label'?: string
}
type Props = OwnProps & {
  size: Size
}

// Names that render a styled span instead of an icon glyph.
const spanNames = new Set(['build_chain', 'dropdownTrigger', 'dropdownTriggerSpecial'])

// ponytail: the `name` prop is public (ReactUI.renderAnimatingIcon), so an unmapped name
// is dynamically imported from @jetbrains/icons on demand; a missing name renders nothing
// rather than crash. Add it to automaton/passthrough to make it static.
const lazyIcons: Record<string, IconType> = {}
const lazyIcon = (name: string): IconType =>
  (lazyIcons[name] ??= React.lazy(() =>
    import(`@jetbrains/icons/${name}.svg?react`).catch(() => ({default: () => null})),
  ) as IconType)

class Icon extends React.PureComponent<Props> {
  static defaultProps = {
    size: 'S',
  }

  render() {
    const {name, className, isVisible, size, ...restProps} = this.props
    const isSvgSource = /^<svg/.test(name)
    const [myIcon, classes] = isSvgSource ? [null, ''] : getIconAndClassNames(name, size === 'M')
    const allClasses = classnames(iconStyles.tcIcon, className, classes, {
      [iconStyles.animate]: isVisible,
    })

    if (spanNames.has(name)) {
      return <span {...restProps} className={allClasses} />
    }

    const passthroughKey = size === 'M' && !isSvgSource ? `${name}-20px` : name
    const resolved = isSvgSource ? name : (myIcon ?? passthroughIcons[passthroughKey])

    if (resolved == null) {
      const LazyIcon = lazyIcon(passthroughKey)
      return (
        <React.Suspense fallback={null}>
          <SvgIcon {...restProps} icon={LazyIcon} data-test-icon={name} className={allClasses} />
        </React.Suspense>
      )
    }

    const iconProps = {
      ...restProps,
      icon: resolved,
      'data-test-icon': isSvgSource ? undefined : name,
    }
    const withPerson = !isSvgSource && (name.match('personal_running') || name.match('my_running'))
    const isDebug = !isSvgSource && name.match('bug_running')
    const detached = !isSvgSource && name.match('detached')

    if (withPerson || detached || isDebug) {
      return (
        <div className={classnames(allClasses, iconStyles.wrapper)}>
          <SvgIcon
            {...iconProps}
            className={classnames((withPerson || isDebug) && iconStyles.staticPerson)}
          />
          <SvgIcon
            {...iconProps}
            className={classnames({
              [iconStyles.detached]: !withPerson && !isDebug,
              [iconStyles.spinningPerson]: withPerson,
              [iconStyles.animate]: isVisible,
              [iconStyles.spinningDebug]: isDebug,
            })}
          />
        </div>
      )
    }

    return <SvgIcon {...iconProps} className={allClasses} />
  }
}

export default compose(
  React.memo,
  branch(
    (props: Props) => props.name.includes('running'),
    withVisibility(null, 'span') as ComponentEnhancer<Props, Props>,
  ),
)(Icon)
